import React from 'react';
import { useTranslation } from 'react-i18next';
import { useLanguage, LANGUAGES } from '../contexts/LanguageContext';
import { Button } from './ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import { Globe, Check } from 'lucide-react';

export default function LanguageSwitcher({ variant = 'icon' }) {
  const { t } = useTranslation();
  const { language, setLanguage } = useLanguage();

  const currentLang = LANGUAGES.find(l => l.code === language) || LANGUAGES[0];
  const otherLang = LANGUAGES.find(l => l.code !== language) || LANGUAGES[1];

  // Flag dropdown: current flag as the trigger, every language in the menu.
  // Used in the header. The old 'toggle' variant cannot reach a third language
  // (otherLang is just the first non-current entry), so Spanish was unreachable.
  if (variant === 'flag') {
    return (
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            variant="ghost"
            size="icon"
            className="h-[48px] w-[48px] sm:h-10 sm:w-10 p-0 flex-shrink-0"
            data-testid="language-switcher-flag"
            aria-label={t('tooltips.switchLanguage')}
          >
            <img
              src={currentLang.flagImg}
              alt={currentLang.name}
              className="w-6 h-4 object-cover rounded-sm"
            />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          {LANGUAGES.map((lang) => (
            <DropdownMenuItem
              key={lang.code}
              onClick={() => setLanguage(lang.code)}
              className="flex items-center justify-between gap-3 cursor-pointer min-h-[48px]"
              data-testid={`lang-option-${lang.code}`}
            >
              <div className="flex items-center gap-2">
                <img src={lang.flagImg} alt="" className="w-5 h-3.5 object-cover rounded-sm" />
                <span>{lang.name}</span>
              </div>
              {language === lang.code && <Check className="h-4 w-4 text-primary" />}
            </DropdownMenuItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>
    );
  }

  // Toggle variant: shows current flag, one click switches to the other language
  if (variant === 'toggle') {
    return (
      <Button
        variant="ghost"
        size="icon"
        className="h-11 w-11 sm:h-10 sm:w-10 p-0"
        onClick={() => setLanguage(otherLang.code)}
        title={`Switch to ${otherLang.name}`}
        tooltip={t('tooltips.switchLanguage')}
        data-testid="language-toggle"
      >
        <img
          src={currentLang.flagImg}
          alt={currentLang.name}
          className="w-6 h-4 object-cover rounded-sm"
        />
      </Button>
    );
  }

  if (variant === 'full') {
    return (
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="outline" className="gap-2" data-testid="language-switcher-full">
            <img src={currentLang.flagImg} alt={currentLang.name} className="w-5 h-3.5 object-cover rounded-sm" />
            <span>{currentLang.name}</span>
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          {LANGUAGES.map((lang) => (
            <DropdownMenuItem
              key={lang.code}
              onClick={() => setLanguage(lang.code)}
              className="flex items-center justify-between gap-3 cursor-pointer"
              data-testid={`lang-option-${lang.code}`}
            >
              <div className="flex items-center gap-2">
                <img src={lang.flagImg} alt={lang.name} className="w-5 h-3.5 object-cover rounded-sm" />
                <span>{lang.name}</span>
              </div>
              {language === lang.code && (
                <Check className="h-4 w-4 text-primary" />
              )}
            </DropdownMenuItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>
    );
  }

  // Default icon variant
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="icon" data-testid="language-switcher" aria-label={t('tooltips.switchLanguage')}>
          <Globe className="h-5 w-5" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        {LANGUAGES.map((lang) => (
          <DropdownMenuItem
            key={lang.code}
            onClick={() => setLanguage(lang.code)}
            className="flex items-center justify-between gap-3 cursor-pointer"
            data-testid={`lang-option-${lang.code}`}
          >
            <div className="flex items-center gap-2">
              <img src={lang.flagImg} alt={lang.name} className="w-5 h-3.5 object-cover rounded-sm" />
              <span>{lang.name}</span>
            </div>
            {language === lang.code && (
              <Check className="h-4 w-4 text-primary" />
            )}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
